/**
 * Helpers, mixin e attributi condivisi per i <v-date-picker> / <v-calendar>.
 *
 * In fondo al file vivono `select_attrs` / `drag_attrs`, gli attributi di
 * highlight della selezione usati da tutti i date picker dei vari siti, e
 * `stay_window` / `stay_limit_disabled_dates`, che traducono un limite di
 * durata del soggiorno in range da passare a `:disabled-dates`.
 *
 * La prima parte riguarda invece le frecce custom di navigazione anno sui
 * <v-date-picker> che espongono uno scoped slot #header.
 *
 * Problema: page.move(±12) di v-calendar viene silenziosamente rigettato
 * quando il mese target è interamente fuori da min/max, lasciando la freccia
 * "inerte" ai bordi dell'anno. Inoltre, un predicato ingenuo come
 * `page.year <= parseInt(min)` confronta solo l'anno e ignora la precisione a
 * livello di mese del bound.
 *
 * Le funzioni pure implementano un clamp consapevole del mese:
 *   - move_picker_by_years() posiziona il picker sul primo/ultimo mese valido quando
 *     l'anno target è solo parzialmente dentro il range.
 *   - is_at_year_bound() disabilita la freccia solo quando un click non
 *     produrrebbe spostamento visibile (clamp result == page corrente).
 *
 * Il mixin `calendar_mixin` espone queste funzioni come methods e aggiunge
 * `move_datepicker_by_years(ref_name, ...)` che risolve `this.$refs[ref_name]` prima
 * di delegare a `move_picker_by_years`.
 *
 * Contratto verso il consumer:
 *   - `ref_name` — chiave stringa del ref del picker sul componente host
 *     (es. 'datepicker_' + index in LF, 'datepicker:' + pIndex in VV).
 *   - `page` — l'oggetto fornito dallo scoped slot #header di v-calendar,
 *     con `{ year, month }` (month è 1-indexed).
 *   - `bounds` — `{ min, max }` stringhe in formato 'YYYY-MM-DD'.
 *
 * Utilizzo nel componente:
 *   import { calendar_mixin } from '@/utilities/calendar'
 *   export default {
 *     mixins: [calendar_mixin],
 *     ...
 *   }
 *
 * Le funzioni sono anche importabili singolarmente per unit test:
 *   import { clamp_year_target, is_at_year_bound, move_picker_by_years } from '@/utilities/calendar'
 *   import { stay_window, stay_limit_disabled_dates } from '@/utilities/calendar'
 *
 * Consumer attuali (al 2026-09-02):
 *   - src/sites/liveflight/components/blocks/details/travelers-details-form.vue
 *   - src/sites/alpitour/components/views/content-blocks/quote/passenger-form.vue
 *   - src/sites/liveeps/components/blocks/booking-engine.vue
 *   - src/sites/bookingv3/components/blocks/booking-engine.vue
 */

import moment from 'moment';
import { log } from '@/utilities';

export function move_picker(picker, to) {
  if (!picker || !picker.move) {
    return Promise.resolve();
  }
  return picker.move(to).catch((e) => {
    log('error on move_picker', e);
  });
}

/**
 * Calcola il (year, month) su cui atterrerebbe il click di una freccia
 * anno, clampando al mese più vicino dentro il range quando il mese
 * target è interamente fuori dai bound.
 *
 * @param {number} years_delta - -1 (indietro) o +1 (avanti)
 * @param {{year: number, month: number}} page - month è 1-indexed
 * @param {{min: string, max: string}} bounds - 'YYYY-MM-DD'
 * @returns {{year: number, month: number}}
 */
export function clamp_year_target(years_delta, page, bounds) {
  const min = new Date(bounds.min);
  const max = new Date(bounds.max);
  const target_year = page.year + years_delta;
  const target_month = page.month;
  const target_start = new Date(target_year, target_month - 1, 1);
  const target_end = new Date(target_year, target_month, 0);
  if (target_end < min) {
    return { year: min.getFullYear(), month: min.getMonth() + 1 };
  }
  if (target_start > max) {
    return { year: max.getFullYear(), month: max.getMonth() + 1 };
  }
  return { year: target_year, month: target_month };
}

/**
 * True quando un click sulla freccia anno non produrrebbe alcun cambio
 * di pagina visibile — usato per pilotare `:disabled` del bottone.
 *
 * @param {number} direction - -1 (‹) o +1 (›)
 */
export function is_at_year_bound(direction, page, bounds) {
  const target = clamp_year_target(direction, page, bounds);
  return target.year === page.year && target.month === page.month;
}

/**
 * Esegue il salto di navigazione anno sul picker passato, clampando al
 * primo mese valido quando l'anno target è parzialmente dentro il range.
 * No-op se il picker è falsy (componente non ancora montato).
 *
 * @param {{move: Function}} picker - v-calendar instance con .move()
 * @param {number} years_delta - -1 (indietro) o +1 (avanti)
 */
export function move_picker_by_years(picker, years_delta, page, bounds) {
  if (!picker) return;
  const target = clamp_year_target(years_delta, page, bounds);
  return picker.move({ month: target.month, year: target.year }).catch((e) => {
    log('error on move_picker_by_years', e);
  });
}

export const calendar_mixin = {
  methods: {
    clamp_year_target,
    is_at_year_bound,
    /**
     * Thin wrapper che risolve `this.$refs[ref_name]` e delega a move_picker_by_years.
     *
     * @param {string} ref_name - chiave del ref del picker sul componente host
     * @param {number} years_delta - -1 (indietro) o +1 (avanti)
     */
    move_datepicker_by_years(ref_name, years_delta, page, bounds) {
      const ref = this.$refs[ref_name];
      const picker = Array.isArray(ref) ? ref[0] : ref;
      return move_picker_by_years(picker, years_delta, page, bounds);
    },
  },
};

/**
 * Attributi di stile condivisi per l'highlight della selezione sui
 * <v-date-picker>/<v-calendar> in modalità range.
 *
 * `select_attrs` è il range già confermato (estremi pieni), `drag_attrs` è il
 * range in trascinamento (estremi con solo il bordo). Entrambi si agganciano a
 * `var(--primary)`, quindi seguono automaticamente il tema del sito.
 *
 * Utilizzo nel componente:
 *   import { select_attrs, drag_attrs } from '@/utilities/calendar'
 *   data() { return { select_attrs, drag_attrs } }
 *
 *   <v-date-picker :select-attribute="select_attrs" :drag-attribute="drag_attrs" />
 *
 * Sono oggetti condivisi fra tutte le istanze: vanno trattati come read-only.
 */
export const select_attrs = {
  highlight: {
    start: { fillMode: 'solid', style: { backgroundColor: 'var(--primary)' } },
    base: { style: { backgroundColor: 'var(--primary)', opacity: '0.25' } },
    end: { fillMode: 'solid', style: { backgroundColor: 'var(--primary)' } },
  },
};

export const drag_attrs = {
  highlight: {
    start: { fillMode: 'outline', style: { 'border-style': 'solid', 'border-width': '2px', 'border-color': 'var(--primary)' } },
    base: { style: { backgroundColor: 'var(--primary)', opacity: '0.25' } },
    end: { fillMode: 'outline', style: { 'border-style': 'solid', 'border-width': '2px', 'border-color': 'var(--primary)' } },
  },
};

/**
 * Finestra di date selezionabili attorno a una data di ancoraggio, dato un
 * limite di durata espresso in notti. La finestra è bilaterale: il secondo
 * click su un range picker può cadere prima o dopo l'ancora, perché
 * v-calendar ordina gli estremi invece di ricominciare la selezione.
 *
 * @param {Date|string} anchor - data di ancoraggio (il primo click)
 * @param {number} max_nights - numero massimo di notti
 * @returns {{min: string, max: string}|null} 'YYYY-MM-DD', null se nessun vincolo
 */
export function stay_window(anchor, max_nights) {
  if (!anchor || !max_nights) return null;
  const start = moment(anchor);
  if (!start.isValid()) return null;
  return {
    min: start.clone().subtract(max_nights, 'days').format('YYYY-MM-DD'),
    max: start.clone().add(max_nights, 'days').format('YYYY-MM-DD'),
  };
}

/**
 * Traduce il limite di durata in due range open-ended da passare a
 * `:disabled-dates` di v-calendar: tutto ciò che precede la finestra e tutto
 * ciò che la segue. I bordi stanno esattamente un giorno fuori da
 * `stay_window`, quindi gli estremi della finestra restano selezionabili.
 *
 * @param {Date|string} anchor - data di ancoraggio (il primo click)
 * @param {number} max_nights - numero massimo di notti
 * @returns {Array<{start: ?string, end: ?string}>} vuoto se nessun vincolo
 */
export function stay_limit_disabled_dates(anchor, max_nights) {
  const bounds = stay_window(anchor, max_nights);
  if (!bounds) return [];
  return [
    {
      start: null,
      end: moment(bounds.min).subtract(1, 'days').format('YYYY-MM-DD'),
    },
    {
      start: moment(bounds.max).add(1, 'days').format('YYYY-MM-DD'),
      end: null,
    },
  ];
}
